import {
  Card,
  Col,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";

import Loader from "components/ui/Loader";
import ErrorHandler from "components/ui/ErrorHandler";

import "./OrganizationStats.scss";
import { useFetchOrganizationStats } from "features/organization/services";
import { useContext } from "react";
import { OrganizationContext } from "contexts/OrganizationProvider";

function OrganizationStats() {
  const { stats, isLoading, error } = useFetchOrganizationStats();
  const organization = useContext(OrganizationContext);

  const {
    hasActionItemMetrics = true,
    hasAssessmentMetrics = true,
    hasEntityMetrics = true,
    hasActionItemTagsMetrics = false,
  } = organization ?? {};

  const theNonActionItemMetricsAreToggledOff =
    hasAssessmentMetrics || hasEntityMetrics;

  if (isLoading) return <Loader />;
  if (error) return <ErrorHandler error={error} />;
  if (!stats) return <></>;

  return (
    <Card className="border-0 mb-4">
      <Table borderless className="mb-0">
        <thead>
          <tr>
            {hasEntityMetrics && (
              <th className="border-end  fw-normal text-nowrap">Entities</th>
            )}
            {hasAssessmentMetrics && (
              <th className="border-end px-2 fw-normal text-nowrap">
                Assessments
              </th>
            )}
            {hasAssessmentMetrics && (
              <th className="border-end px-2 fw-normal text-nowrap">
                Observations
              </th>
            )}
            {hasActionItemMetrics && (
              <th className="px-2 fw-normal text-nowrap">Action Items</th>
            )}
            {hasActionItemTagsMetrics &&
              stats?.actionItemsByTags?.length <= 3 &&
              !theNonActionItemMetricsAreToggledOff && (
                <th className="px-2 fw-normal border-start text-nowrap">
                  Action Items Tags
                </th>
              )}
          </tr>
        </thead>
        <tbody className="media-for-stats-box">
          <tr>
            {hasEntityMetrics && (
              <td className="border-end w-10">
                <Row>
                  <Col md={12}>
                    <h3 className="text-nowrap">{stats?.facilities ?? 0}</h3>
                    <div>
                      <p className="text-nowrap">Total</p>
                    </div>
                  </Col>
                </Row>
              </td>
            )}
            {hasAssessmentMetrics && (
              <td className="border-end w-25">
                <Row>
                  <Col md={4}>
                    <h3 className="text-nowrap">{stats?.audits ?? 0}</h3>
                    <div>
                      <p className="text-nowrap">Total</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <h3 className="text-nowrap">
                      {stats?.completedAudits ?? 0}
                    </h3>
                    <div>
                      <p className="text-nowrap">Done</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <h3 className="text-nowrap">{stats?.lockedAudits ?? 0}</h3>
                    <div>
                      <p className="text-nowrap">Locked</p>
                    </div>
                  </Col>
                </Row>
              </td>
            )}
            {hasAssessmentMetrics && (
              <td className="w-10">
                <Row>
                  <Col md={12} className="">
                    <h3 className="text-nowrap">{stats?.observations ?? 0}</h3>
                    <div>
                      <p className="text-nowrap">Total</p>
                    </div>
                  </Col>
                </Row>
              </td>
            )}
            {hasActionItemMetrics && (
              <td
                className={`w-35 ${hasAssessmentMetrics ? "border-start" : ""}`}
              >
                <Row>
                  <Col md={4} className="">
                    <h3 className="text-nowrap">
                      {stats?.actionItemsByStatus?.complete ?? 0}
                    </h3>
                    <div>
                      <p className="text-nowrap">Submitted</p>
                    </div>
                  </Col>
                  <Col md={4} className="">
                    <h3 className="text-nowrap">
                      {stats?.actionItemsByStatus?.accepted ?? 0}
                    </h3>
                    <div>
                      <p className="text-nowrap">Accepted</p>
                    </div>
                  </Col>
                  <Col md={4} className="">
                    <h3 className="text-nowrap">
                      {stats?.actionItemsByStatus?.overdue ?? 0}
                    </h3>
                    <div>
                      <p className="text-nowrap">Overdue</p>
                    </div>
                  </Col>
                </Row>
              </td>
            )}
            {hasActionItemTagsMetrics &&
              stats?.actionItemsByTags?.length > 0 &&
              stats?.actionItemsByTags?.length <= 3 &&
              !theNonActionItemMetricsAreToggledOff && (
                <td className={`w-35 border-start`}>
                  <Row>
                    {stats?.actionItemsByTags?.map((tag, index) => {
                      const name = tag?.name ?? "";
                      return (
                        <Col key={index}>
                          <h3 className="text-nowrap">{tag?.count ?? 0}</h3>
                          <div>
                            {name.length > 21 && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-tag-${index}`}>
                                    {name}
                                  </Tooltip>
                                }
                              >
                                <p style={{ cursor: "pointer" }}>
                                  {name.slice(0, 21) + "..."}
                                </p>
                              </OverlayTrigger>
                            )}{" "}
                            {name.length < 21 && <p>{name}</p>}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </td>
              )}
          </tr>
        </tbody>
      </Table>
      {hasActionItemTagsMetrics &&
        (stats?.actionItemsByTags?.length > 3 ||
          theNonActionItemMetricsAreToggledOff) && (
          <>
            <hr />
            <div className="py-3 px-2">
              <h6 className="text-nowrap">Action Items Tags</h6>
              <Row className="flex-lg-wrap">
                {stats?.actionItemsByTags?.map(
                  ({ name = "", count = 0 }, index) => (
                    <Col
                      key={index}
                      className="my-2"
                      xs={12}
                      sm={12}
                      md={6}
                      lg={2}
                    >
                      <div className="d-flex flex-column align-items-start">
                        <h3 className="text-nowrap">{count}</h3>
                        {name.length > 21 && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-tag-list-${index}`}>
                                {name}
                              </Tooltip>
                            }
                          >
                            <p
                              style={{ cursor: "pointer" }}
                              className="text-wrap text-break"
                            >
                              {name.slice(0, 21) + "..."}
                            </p>
                          </OverlayTrigger>
                        )}
                        {name.length < 21 && <p>{name}</p>}
                      </div>
                    </Col>
                  ),
                )}
              </Row>
            </div>
          </>
        )}
    </Card>
  );
}

export default OrganizationStats;
